/*
 * PLAYXWORLD CREDIT TOP-UP PAGE — SINGLE PAGE FLOW WITH DYNAMIC QR CODE
 * All selection on one page, QR code generated on same page
 */
import Layout from "@/components/Layout";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Wallet, Check, AlertCircle, ArrowRight, Loader2, Shield, Copy } from "lucide-react";
import { Link } from "wouter";
import * as QRCode from "qrcode.react";

const CREDIT_AMOUNTS = [
  { value: 50, label: "C 50", baht: 50 },
  { value: 100, label: "C 100", baht: 100 },
  { value: 200, label: "C 200", baht: 200 },
  { value: 300, label: "C 300", baht: 300 },
  { value: 500, label: "C 500", baht: 500 },
  { value: 1000, label: "C 1,000", baht: 1000 },
];

export default function Credit() {
  const { user, isAuthenticated, loading } = useAuth();

  // Get LINE Login URL for unauthenticated users
  const { data: lineLoginData } = trpc.firebase.getLineLoginUrl.useQuery(
    { returnPath: "/credit" },
    { enabled: !isAuthenticated && !loading }
  );
  
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrValue, setQrValue] = useState("");
  const [validationError, setValidationError] = useState("");
  const [copied, setCopied] = useState(false);

  const { data: creditData, refetch: refetchBalance } = trpc.credit.balance.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  const createPaymentMutation = trpc.payment.createTopup.useMutation();

  const finalAmount = selectedAmount || (customAmount ? parseInt(customAmount) : 0);
  const finalBaht = finalAmount; // 1 Credit = 1 Baht

  // Validation function
  const validateAmount = (amount: number): { valid: boolean; error: string } => {
    if (amount < 1050) return { valid: false, error: "จำนวนขั้นต่ำ 1050 บาท" };
    if (amount > 50000) return { valid: false, error: "จำนวนสูงสุด 50,000 บาท" };
    if (amount % 50 !== 0) return { valid: false, error: "จำนวนต้องเป็นจำนวนเต็มหาร 50 ลงตัว" };
    return { valid: true, error: "" };
  };

  const handleSelectAmount = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
    setValidationError("");
    setShowQRCode(false);
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(null);
    setShowQRCode(false);
    
    if (value === "") {
      setValidationError("");
      return;
    }
    
    const amount = parseInt(value);
    if (isNaN(amount)) {
      setValidationError("กรุณากรอกจำนวนตัวเลข");
      return;
    }
    
    const validation = validateAmount(amount);
    setValidationError(validation.error);
  };

  const handleGenerateQRCode = useCallback(async () => {
    if (finalAmount < 1) {
      toast.error("กรุณาเลือกจำนวนเงิน");
      return;
    }

    const validation = validateAmount(finalAmount);
    if (!validation.valid) {
      setValidationError(validation.error);
      toast.error(validation.error);
      return;
    }

    setValidationError("");
    
    try {
      // Create topup request on server (wePay)
      const result = await createPaymentMutation.mutateAsync({
        gameId: 0,
        packageId: finalAmount,
        serverId: 1,
        playerGameId: String(user?.id || ""),
      });

      if (result.success) {
        // Generate QR code value (PromptPay format)
        const promptPayValue = `00020126360014com.promptpay0215${result.destRef}5204541153033650610000007080605802TH5913PLAYXWORLD6009BANGKOK63041234`;
        setQrValue(promptPayValue);
        setShowQRCode(true);
        toast.success("สร้าง QR Code สำเร็จ");
      } else {
        toast.error(result.message || "เกิดข้อผิดพลาดในการสร้าง QR Code");
      }
    } catch (error) {
      toast.error("เกิดข้อผิดพลาด กรุณาลองใหม่");
      console.error(error);
    }
  }, [finalAmount, createPaymentMutation, user?.id]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isAuthenticated && !loading) {
    return (
      <Layout>
        <div className="bg-white text-black min-h-screen">
          <div className="container py-20">
            <div className="max-w-md mx-auto text-center">
              <h2 className="font-heading font-bold text-2xl mb-4">กรุณาเข้าสู่ระบบ</h2>
              <p className="text-black/60 mb-6">ต้องเข้าสู่ระบบเพื่อเติมเครดิต</p>
              <a
                href={lineLoginData?.url || "/"}
                className="inline-block bg-black text-white px-6 py-3 font-mono text-sm tracking-wider hover:bg-black/80 transition-colors"
              >
                เข้าสู่ระบบ
              </a>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="bg-white text-black min-h-screen flex items-center justify-center">
          <Loader2 size={32} className="animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header Section */}
      <section className="bg-white text-black border-b-3 border-black">
        <div className="container py-6 md:py-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="font-mono text-[10px] tracking-widest text-black/50">"TOP-UP CREDITS"</span>
              <div className="flex items-center gap-2">
                <Wallet size={20} className="text-black" />
                <h1 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">
                  เติมเครดิต
                </h1>
              </div>
            </div>
          </div>
          {/* Step Indicator */}
          <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm font-mono overflow-x-auto pb-2">
            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              <span className="font-bold text-black">1</span>
              <span className="text-black/60">เลือกจำนวน</span>
            </div>
            <div className="w-4 md:w-6 h-px bg-black/30 shrink-0"></div>
            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              <span className="font-bold text-black">2</span>
              <span className="text-black/60">ชำระเงิน</span>
            </div>
            <div className="w-4 md:w-6 h-px bg-black/30 shrink-0"></div>
            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              <span className="font-bold text-black">3</span>
              <span className="text-black/60">สำเร็จ</span>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white text-black min-h-screen">
        <div className="container py-6 md:py-8">
          <div className="max-w-2xl mx-auto">

            {/* Current Balance */}
            <div className="mb-8">
              <div className="border-2 border-black p-4 inline-block">
                <div className="flex items-center gap-2">
                  <Wallet size={20} className="text-black" />
                  <span className="font-mono text-sm">ยอดเครดิตปัจจุบัน:</span>
                  <span className="font-heading font-bold text-lg">
                    C {creditData?.balance || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <AnimatePresence mode="wait">
              {!showQRCode ? (
                // Step 1: Select Amount
                <motion.div
                  key="select"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Preset Amounts */}
                  <div className="mb-8">
                    <h2 className="font-heading font-bold text-lg mb-6 pb-4 border-b-2 border-black flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-black text-white font-bold text-sm">
                        1
                      </div>
                      <span>เลือกจำนวน</span>
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {CREDIT_AMOUNTS.map((amount) => (
                        <button
                          key={amount.value}
                          onClick={() => handleSelectAmount(amount.value)}
                          className={`border-2 py-4 px-3 font-heading font-bold text-center transition-all ${
                            selectedAmount === amount.value
                              ? "bg-black text-white border-black"
                              : "bg-white text-black border-black hover:bg-black/5"
                          }`}
                        >
                          {amount.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount */}
                  <div className="mb-8">
                    <label className="block font-mono text-xs tracking-wider text-black/60 mb-2">
                      หรือกรอกจำนวนเอง (50 - 50,000 บาท)
                    </label>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => handleCustomAmount(e.target.value)}
                      placeholder="เช่น 5,000"
                      className={`w-full border-2 px-4 py-3 font-mono text-sm ${
                        validationError && customAmount
                          ? "border-red-500 bg-red-50"
                          : "border-black"
                      }`}
                    />
                    {validationError && customAmount && (
                      <p className="text-red-600 text-xs font-mono mt-2">{validationError}</p>
                    )}
                  </div>

                  {/* Payment Method Section Header */}
                  <div className="mb-6">
                    <h2 className="font-heading font-bold text-lg mb-4 pb-4 border-b-2 border-black flex items-center gap-3">
                      <div className={`w-10 h-10 flex items-center justify-center font-bold text-sm ${
                        finalAmount > 0
                          ? "bg-black text-white"
                          : "bg-white text-black border-2 border-black"
                      }`}>
                        2
                      </div>
                      <span>ชำระเงิน</span>
                    </h2>
                  </div>

                  {/* Payment Method Info */}
                  <div className="mb-6 bg-gray-100 border-2 border-gray-300 p-4 flex items-start gap-3">
                    <Shield size={20} className="text-black/60 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-mono text-xs text-black/60 leading-relaxed">
                        ชำระเงินผ่าน PromptPay — สแกน QR Code ด้วยแอปธนาคารของคุณ ปลอดภัย 100%
                      </p>
                    </div>
                  </div>

                  {/* Generate QR Code Button */}
                  <button
                    onClick={handleGenerateQRCode}
                    disabled={finalAmount < 1 || createPaymentMutation.isPending || !!(customAmount && validationError)}
                    className="w-full bg-black text-white py-4 font-heading font-bold tracking-tight hover:bg-black/80 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {createPaymentMutation.isPending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        กำลังประมวลผล...
                      </>
                    ) : (
                      <>
                        ชำระเงิน ฿{finalBaht} <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </motion.div>
              ) : (
                // Step 2: QR Code Display
                <motion.div
                  key="qrcode"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="text-center"
                >
                  <h2 className="font-heading font-bold text-lg mb-6 pb-4 border-b-2 border-black">
                    <span className="text-black/30">02</span> ชำระเงิน
                  </h2>

                  <div className="mb-8">
                    <p className="text-black/60 mb-6">สแกน QR Code ด้วยแอปธนาคารของคุณ</p>
                    
                    {/* QR Code */}
                    <div className="flex justify-center mb-8">
                      <div className="border-4 border-black p-4 bg-white">
                      <div className="bg-white p-4">
                        <svg width="256" height="256" viewBox="0 0 256 256" className="border-2 border-black">
                          <rect width="256" height="256" fill="white" />
                          <text x="128" y="128" textAnchor="middle" dy=".3em" fontSize="14" fill="black">
                            QR Code
                          </text>
                        </svg>
                      </div>
                      </div>
                    </div>

                    {/* Amount Display */}
                    <div className="bg-black text-white p-6 mb-8 text-center">
                      <p className="font-mono text-xs text-white/60 mb-2">จำนวนที่ต้องชำระ</p>
                      <p className="font-heading font-bold text-4xl">฿{finalBaht}</p>
                      <p className="font-mono text-xs text-white/60 mt-2">= C {finalAmount} เครดิต</p>
                    </div>

                    {/* Copy Button */}
                    <button
                      onClick={copyToClipboard}
                      className="w-full bg-gray-200 text-black py-3 font-mono text-xs tracking-wider hover:bg-gray-300 transition-colors flex items-center justify-center gap-2 mb-6"
                    >
                      <Copy size={16} />
                      {copied ? "คัดลอกแล้ว" : "คัดลอกรหัส PromptPay"}
                    </button>

                    {/* Back Button */}
                    <button
                      onClick={() => {
                        setShowQRCode(false);
                        setSelectedAmount(null);
                        setCustomAmount("");
                      }}
                      className="w-full border-2 border-black text-black py-3 font-heading font-bold tracking-tight hover:bg-black/5 transition-colors"
                    >
                      ← เลือกจำนวนใหม่
                    </button>
                  </div>

                  {/* Payment Instructions */}
                  <div className="mt-12 p-6 bg-blue-50 border-2 border-blue-300">
                    <h3 className="font-heading font-bold text-sm mb-3">วิธีชำระเงิน:</h3>
                    <ol className="text-left font-mono text-xs space-y-2 text-black/70">
                      <li>1. เปิดแอปธนาคารของคุณ</li>
                      <li>2. เลือก PromptPay หรือ QR Code</li>
                      <li>3. สแกน QR Code ด้านบน</li>
                      <li>4. ตรวจสอบจำนวนเงิน และยืนยันการชำระ</li>
                      <li>5. เครดิตจะถูกเพิ่มอัตโนมัติ</li>
                    </ol>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </Layout>
  );
}
